import {Box, Tooltip} from '@mui/material'
import {FC, ReactNode, useEffect, RefObject, useRef, useState, useCallback} from 'react'

import {RESIZE_HANDLE_WIDTH_IN_PX} from '../../consts'

type ResizeHandleProps = {
  handleResizeStart: () => void
  handleResizeEnd?: () => void
  placement?: 'top' | 'bottom'
  containerRef?: RefObject<HTMLDivElement>
  tooltipBody?: ReactNode
  isTooltipOpen?: boolean
  'data-test-id': string
  isPinned: boolean
  setIsPinned: (value: boolean) => void
}

const ARROW_MARGIN = 4

export const ResizeHandle: FC<ResizeHandleProps> = ({
  handleResizeStart,
  handleResizeEnd,
  tooltipBody,
  isTooltipOpen,
  containerRef,
  placement = 'bottom',
  'data-test-id': dataTestId,
  isPinned,
  setIsPinned
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const showTooltip = isTooltipOpen !== undefined ? isTooltipOpen : isPinned || isHovered
  const timeoutRef = useRef<number>()
  const handleRef = useRef<HTMLDivElement | null>(null)
  const tooltipContentRef = useRef<HTMLDivElement | null>(null)

  const toggleHover = (value: boolean) => {
    if (isPinned) return
    clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => setIsHovered(value), 100)
  }

  const isEventInside = (target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Node)) return false
    const inHandle = handleRef.current?.contains(target) ?? false
    const inTooltip = tooltipContentRef.current?.contains(target) ?? false
    return inHandle || inTooltip
  }

  const handleDocumentMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!isPinned) return
      if (!isEventInside(e.target)) {
        setIsPinned(false)
        if (handleRef.current && !handleRef.current.matches(':hover')) {
          setIsHovered(false)
        }
      }
    },
    [isPinned, setIsPinned]
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentMouseDown)

    return () => {
      clearTimeout(timeoutRef.current)
      document.removeEventListener('mousedown', handleDocumentMouseDown)
    }
  }, [handleDocumentMouseDown])

  return (
    <Tooltip
      title={
        tooltipBody ? (
          <Box
            ref={tooltipContentRef}
            onMouseMove={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              e.stopPropagation()
              if (!isPinned) setIsPinned(false)
            }}
            onMouseEnter={() => toggleHover(true)}
            onMouseLeave={() => toggleHover(false)}
          >
            {tooltipBody}
          </Box>
        ) : undefined
      }
      open={showTooltip}
      placement={placement}
      PopperProps={{
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              boundary: containerRef?.current || 'clippingParents'
            }
          }
        ]
      }}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            padding: 0
          }
        }
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          width: RESIZE_HANDLE_WIDTH_IN_PX,
          minWidth: RESIZE_HANDLE_WIDTH_IN_PX,
          cursor: 'ew-resize',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseDown={handleResizeStart}
        onMouseUp={handleResizeEnd}
        onClick={() => {
          setIsPinned(true)
          setIsHovered(false)
        }}
        onMouseEnter={() => toggleHover(true)}
        onMouseLeave={() => toggleHover(false)}
        data-test-id={dataTestId}
      >
        <Box
          sx={{
            position: 'relative',
            width: 2,
            height: '100%',
            backgroundColor: 'grey.800',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              borderWidth: 4,
              borderStyle: 'solid',
              borderColor: 'transparent',
              borderTopWidth: 3,
              borderBottomWidth: 3
            },
            '&::before': {
              right: ARROW_MARGIN,
              borderRightColor: 'black'
            },
            '&::after': {
              left: ARROW_MARGIN,
              borderLeftColor: 'black'
            }
          }}
        />
      </Box>
    </Tooltip>
  )
}
